import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CalendarDays, Check, X, Users, Phone, Coins, QrCode, Plus } from "lucide-react";

const STATUS_COLORS = {
  inquiry:          "border-foreground/20 text-foreground/60",
  awaiting_deposit: "border-amber-500/40 text-amber-500",
  confirmed:        "border-foreground/50 text-foreground",
  checked_in:       "border-emerald-500/50 text-emerald-500",
  seated:           "border-emerald-500/70 text-emerald-500",
  completed:        "border-foreground/30 text-foreground/50",
  cancelled:        "border-red-500/40 text-red-500",
  no_show:          "border-red-500/40 text-red-500",
};

export default function ReservationsCalendarPage() {
  const { branchId, hasPerm } = useAuth();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0,10));
  const [items, setItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", guest_count: 2,
    starts_at: "", duration_minutes: 180, table_id: "", notes: "",
  });

  const load = async () => {
    try {
      const params = { date };
      if (branchId) params.branch_id = branchId;
      const [r, s] = await Promise.all([
        api.get("/reservations", { params }),
        api.get("/reservations-stats", { params: branchId ? { branch_id: branchId } : {} }),
      ]);
      setItems(r.data); setStats(s.data);
      if (branchId) {
        const t = await api.get("/tables", { params: { branch_id: branchId } });
        setTables(t.data);
      }
    } catch (_) {} finally { setLoading(false); }
  };
  useEffect(() => { setLoading(true); load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [date, branchId]);

  const grouped = useMemo(() => {
    const buckets = {};
    items.forEach((r) => {
      const h = new Date(r.starts_at).getHours();
      const bucket = h < 4 ? "01-04" : h < 18 ? "before-18" : `${h}:00`;
      (buckets[bucket] ||= []).push(r);
    });
    return Object.entries(buckets).sort();
  }, [items]);

  const submit = async (e) => {
    e.preventDefault();
    if (!branchId) { toast.error("Pick a branch first"); return; }
    try {
      await api.post("/reservations", {
        ...form,
        branch_id: branchId,
        starts_at: new Date(form.starts_at).toISOString(),
        guest_count: parseInt(form.guest_count),
        duration_minutes: parseInt(form.duration_minutes),
        table_id: form.table_id || null,
      });
      toast.success("Reservation created");
      setCreating(false); load();
      setForm({ customer_name: "", customer_phone: "", guest_count: 2, starts_at: "", duration_minutes: 180, table_id: "", notes: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (r, status, extra = {}) => {
    try {
      await api.post(`/reservations/${r.id}/status`, { status, ...extra });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const checkin = async (r) => {
    try {
      await api.post(`/reservations/${r.id}/checkin`);
      toast.success(`${r.customer_name} checked in`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addDeposit = async (r) => {
    const amount = parseFloat(prompt("Deposit amount (₱)?"));
    if (!amount) return;
    const method = prompt("Method? (cash / gcash / maya / credit_card / bank_transfer)", "cash") || "cash";
    try { await api.post(`/reservations/${r.id}/deposit`, { amount, method }); toast.success("Deposit recorded"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="reservations-calendar-page">
      {/* Header row */}
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Reservations</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Tonight's bookings</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Detects overlapping reservations, tracks deposits (applied once), and syncs table status live.
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">Expected guests</div>
          <div className="mt-3 display-heading text-5xl ink mono">{stats?.expected_guests ?? 0}</div>
          <div className="mt-1 tiny-label ink-subtle">
            {stats?.tonight_bookings ?? 0} bookings · {stats?.checked_in ?? 0} checked in
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-10 flex items-center justify-between gap-4 flex-wrap animate-fade-up animate-fade-up-1">
        <div className="flex items-center gap-3">
          <CalendarDays size={16} className="ink-subtle"/>
          <input
            type="date"
            value={date}
            onChange={(e)=>setDate(e.target.value)}
            data-testid="reservations-date"
            className="h-10 rounded-md border hairline surface-card px-3 text-sm ink mono focus:border-foreground/50 outline-none"
          />
          <span className="tiny-label ink-subtle">{items.length} on {new Date(date).toLocaleDateString("en-PH", { weekday: "long", month: "long", day: "numeric" })}</span>
        </div>
        {hasPerm("reservation.manage") && (
          <button
            onClick={() => setCreating(true)}
            data-testid="new-reservation-btn"
            className="btn-pill btn-pill-solid h-10"
          >
            <Plus size={14}/> New booking
          </button>
        )}
      </div>

      {/* Timeline */}
      <div className="mt-12 space-y-8 animate-fade-up animate-fade-up-2">
        {loading && <div className="ink-subtle text-sm">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="border hairline-soft rounded-md p-12 text-center ink-subtle text-sm">
            No reservations yet for this date. Click <span className="ink">New booking</span> to add one.
          </div>
        )}
        {grouped.map(([slot, rows]) => (
          <div key={slot}>
            <div className="flex items-center gap-3 mb-4">
              <div className="section-num">{slot}</div>
              <div className="h-px flex-1 bg-border/60"/>
              <div className="tiny-label">{rows.length} booking(s)</div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {rows.map((r) => (
                <ReservationCard key={r.id} r={r} tables={tables}
                                  onCheckin={checkin}
                                  onStatus={setStatus}
                                  onDeposit={addDeposit}/>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Create drawer */}
      {creating && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="new-reservation-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={()=>setCreating(false)}/>
          <form onSubmit={submit} className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="section-eyebrow">Reservations</div>
            <h2 className="mt-2 display-heading text-3xl ink">New booking</h2>
            <F label="Customer name"><input required value={form.customer_name} onChange={(e)=>setForm({...form, customer_name: e.target.value})} className="i" data-testid="res-name"/></F>
            <F label="Phone"><input value={form.customer_phone} onChange={(e)=>setForm({...form, customer_phone: e.target.value})} className="i" data-testid="res-phone" placeholder="+63 917 …"/></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Guests"><input type="number" min={1} required value={form.guest_count} onChange={(e)=>setForm({...form, guest_count: e.target.value})} className="i" data-testid="res-guests"/></F>
              <F label="Duration (min)"><input type="number" min={30} step={15} required value={form.duration_minutes} onChange={(e)=>setForm({...form, duration_minutes: e.target.value})} className="i"/></F>
            </div>
            <F label="Date & time">
              <input type="datetime-local" required value={form.starts_at} onChange={(e)=>setForm({...form, starts_at: e.target.value})} className="i" data-testid="res-datetime"/>
            </F>
            <F label="Table (optional)">
              <select value={form.table_id} onChange={(e)=>setForm({...form, table_id: e.target.value})} className="i" data-testid="res-table">
                <option value="">— Assign later —</option>
                {tables.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.zone} · cap {t.capacity}{t.vip ? " · VIP" : ""}</option>)}
              </select>
            </F>
            <F label="Notes"><textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="i"/></F>
            <div className="mt-6 flex gap-2">
              <button type="button" onClick={()=>setCreating(false)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="res-submit" className="btn-pill btn-pill-solid h-11 flex-1">Create booking</button>
            </div>
            <style>{`.i { margin-top: 6px; height: 44px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-family: 'JetBrains Mono', monospace; font-size: 13px; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

function ReservationCard({ r, tables, onCheckin, onStatus, onDeposit }) {
  const table = tables.find((t) => t.id === r.table_id);
  const t = new Date(r.starts_at);
  const time = t.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" });
  const color = STATUS_COLORS[r.status] || "border-foreground/20 text-foreground/60";
  const isActive = ["confirmed", "awaiting_deposit", "inquiry"].includes(r.status);
  const isCheckedIn = r.status === "checked_in";

  return (
    <div className="border hairline surface-card rounded-lg p-5" data-testid={`res-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="mono ink text-lg font-semibold">{time}</div>
            <span className={`text-[9px] uppercase tracking-[0.24em] font-bold px-2 py-0.5 rounded-full border ${color}`}>
              {r.status.replace("_"," ")}
            </span>
          </div>
          <div className="mt-2 display-heading text-lg ink">{r.customer_name}</div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ink-muted">
            <span className="flex items-center gap-1.5"><Users size={11}/> {r.guest_count} guests</span>
            {r.customer_phone && <span className="flex items-center gap-1.5"><Phone size={11}/> {r.customer_phone}</span>}
            {table && <span className="flex items-center gap-1.5">· {table.name} ({table.zone})</span>}
            <span className="mono ink-subtle">{r.confirmation_code}</span>
          </div>
          {r.notes && <div className="mt-2 text-xs ink-subtle italic">"{r.notes}"</div>}
        </div>
      </div>

      {/* Deposit strip */}
      {(r.deposit_paid > 0 || isActive) && (
        <div className="mt-4 flex items-center justify-between border-t hairline-soft pt-3">
          <div className="flex items-center gap-2 text-xs ink-muted">
            <Coins size={12}/>
            <span>Deposit</span>
            <span className="mono ink">{peso(r.deposit_paid || 0)}</span>
            {r.deposit_applied && <span className="text-[9px] uppercase tracking-[0.2em] ink-subtle">applied</span>}
          </div>
          {!r.deposit_applied && isActive && (
            <button onClick={() => onDeposit(r)} data-testid={`res-deposit-${r.id}`} className="text-[10px] uppercase tracking-[0.24em] font-semibold ink-muted hover:ink transition-colors">
              + Record
            </button>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        {isActive && (
          <button onClick={() => onCheckin(r)} data-testid={`res-checkin-${r.id}`} className="btn-pill btn-pill-solid h-8 px-3 text-[10px]">
            <QrCode size={11}/> Check-in
          </button>
        )}
        {isCheckedIn && (
          <button onClick={() => onStatus(r, "seated")} data-testid={`res-seat-${r.id}`} className="btn-pill btn-pill-solid h-8 px-3 text-[10px]">
            <Check size={11}/> Seat
          </button>
        )}
        {r.status === "seated" && (
          <button onClick={() => onStatus(r, "completed")} className="btn-pill btn-pill-outline h-8 px-3 text-[10px]">
            Complete
          </button>
        )}
        {isActive && (
          <>
            <button onClick={() => { const reason = prompt("Reason?"); if (reason) onStatus(r, "no_show", { reason }); }} className="btn-pill btn-pill-outline h-8 px-3 text-[10px]">No-show</button>
            <button onClick={() => { const reason = prompt("Reason?"); if (reason) onStatus(r, "cancelled", { reason }); }} className="btn-pill btn-pill-outline h-8 px-3 text-[10px]">
              <X size={11}/> Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
